import { type fabric } from 'fabric';
import { type IPoint } from 'fabric/fabric-impl';
import { COLORS } from '../constants';
import { type Constructor } from '../types';

interface HighlightBehaviors {
  // use to turn highlights on/off for the next render
  highlight(): void;
  clearHighlight(): void;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function HighlightBehaviors(Base: Constructor<fabric.Object>): any {
  return class WithHighlightBehaviors
    extends Base
    implements HighlightBehaviors
  {
    __hasHighlightBehaviors = true;

    private highlightLevel: number | undefined;

    highlight() {
      this.highlightLevel = 1;
      this.canvas?.requestRenderAll();
    }

    clearHighlight() {
      this.highlightLevel = 0;
      this.canvas?.requestRenderAll();
    }

    private _renderHighlight(
      ctx: CanvasRenderingContext2D,
      options: { level: number; stroke: boolean; offset?: IPoint }
    ) {
      const x = options.offset?.x ?? -this.width / 2;
      const y = options.offset?.y ?? -this.height / 2;

      ctx.save();

      ctx.beginPath();

      ctx.moveTo(x, y);
      ctx.lineTo(x + this.width, y);
      ctx.lineTo(x + this.width, y + this.height);
      ctx.lineTo(x, y + this.height);
      ctx.closePath();

      ctx.fillStyle = '#5ca5e0';
      ctx.globalAlpha = 0.5 * (options.level ?? 0);
      ctx.fill();

      if (options.stroke) {
        ctx.scale(1 / this.scaleX, 1 / this.scaleY);
        ctx.lineWidth = 2;
        ctx.globalAlpha = options.level ?? 0;
        ctx.strokeStyle = COLORS['--aha-blue-500'];
        ctx.stroke();
      }

      ctx.restore();
    }

    // should be called manually for objects that don't render in the standard way
    renderHighlight(ctx: CanvasRenderingContext2D, offset?: IPoint) {
      if (this.highlightLevel) {
        this._renderHighlight(ctx, {
          level: this.highlightLevel ?? 1,
          stroke: true,
          offset,
        });
      }
    }

    _render(ctx: CanvasRenderingContext2D) {
      super._render(ctx);

      this.renderHighlight(ctx);
    }
  };
}

export default HighlightBehaviors;
